import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ViewAllVetsModal from "../../components/modals/ViewAllVetsModal";
import InviteVetByEmailModal from "../../components/modals/InviteVetByEmailModal";
import Navbar from "../../components/account/Navbar";
import {
  showSuccessToast,
  showErrorToast,
  showInfoToast,
} from "../../utils/notificationUtils";
import "../../styles/clinicsetup.css";
import "../../pages/reception/ReceptionStyles.css";

const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";

const ManageTeam = ({ clinicData, clinicId }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [showViewAllModal, setShowViewAllModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState("");

  // Invitation modal states
  const [showInvitationModal, setShowInvitationModal] = useState(false);
  const [selectedVet, setSelectedVet] = useState(null);
  const [invitationMessage, setInvitationMessage] = useState("");
  const [isSendingInvitation, setIsSendingInvitation] = useState(false);
  const [invitationError, setInvitationError] = useState("");

  // Cancel confirmation modal states
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [invitationToCancel, setInvitationToCancel] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);

  // Remove confirmation modal states
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState(null);
  const [isRemoving, setIsRemoving] = useState(false);

  // Current veterinarians states
  const [currentVeterinarians, setCurrentVeterinarians] = useState([]);
  const [isLoadingVets, setIsLoadingVets] = useState(false);
  const [vetsError, setVetsError] = useState("");

  // Email invitation modal states
  const [showEmailInviteModal, setShowEmailInviteModal] = useState(false);
  const [inviteUserType, setInviteUserType] = useState("VETERINARIAN"); // VETERINARIAN or CLINIC_MODERATOR

  // Get clinic ID from centralized data

  // Function to search veterinarians
  const searchVeterinarians = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    setSearchError("");

    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(
        `${API_BASE_URL}/api/v1/veterinarians/filter?not_in_clinic=1&name=${encodeURIComponent(
          query
        )}`,
        {
          headers: {
            Accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      if (!response.ok) {
        // Try to parse the error message from the backend
        let errorMessage = t("manageTeam.errors.searchFailed");
        try {
          const errorData = await response.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (e) {
          // If parsing fails, append the likely cause to the default message
          errorMessage += ". " + t("manageTeam.errors.alreadyInClinicHint", { defaultValue: "The user is likely already part of a different clinic." });
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      if (result.status === "success") {
        // Ensure data is an array before filtering
        const searchData = Array.isArray(result.data) ? result.data : [];

        // Filter out ADMIN users from search results
        const filteredResults = searchData.filter(
          vet => vet && (vet.user_type !== 'ADMIN' && vet.user_type !== 'admin')
        );
        setSearchResults(filteredResults);
        if (filteredResults.length === 0) {
          setSearchError(
            t("manageTeam.search.noResultsSpecific", { query })
          );
        }
      } else {
        const errorMessage =
          result.message || t("manageTeam.errors.genericSearch");
        setSearchError(errorMessage);
        showErrorToast(errorMessage, {
          title: t("manageTeam.toasts.searchFailed.title"),
          duration: 4000,
        });
      }
    } catch (error) {
      console.error("Error searching veterinarians:", error);
      // Use the error message from the catch block if it was thrown above, otherwise use default
      const errorMessage = error.message && error.message !== "Failed to fetch"
        ? error.message
        : t("manageTeam.errors.searchFailed") + ". " + t("manageTeam.errors.alreadyInClinicHint", { defaultValue: "The user is likely already part of a different clinic." });

      setSearchError(errorMessage);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Function to fetch current veterinarians - always fetch fresh data
  const fetchCurrentVeterinarians = async () => {
    if (!clinicId) {
      const errorMessage = t("manageTeam.errors.missingClinic");
      setVetsError(errorMessage);
      showErrorToast(errorMessage, {
        title: t("manageTeam.toasts.setupRequired.title"),
        duration: 5000,
      });
      return;
    }

    setIsLoadingVets(true);
    setVetsError("");

    // Always fetch fresh data from API to prevent showing stale members
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(
        `${API_BASE_URL}/api/v1/clinic-members?clinic_id=${clinicId}`,
        {
          headers: {
            Accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      if (!response.ok) {
        throw new Error(t("manageTeam.errors.fetchMembers"));
      }

      const result = await response.json();
      if (result.status === "success") {
        setCurrentVeterinarians(result.data || []);
      } else {
        // Fallback to centralized data only if API fails
        if (clinicData?.clinic_members) {
          setCurrentVeterinarians(clinicData.clinic_members);
        } else {
          const errorMessage =
            result.message || t("manageTeam.errors.fetchMembers");
          setVetsError(errorMessage);
          showErrorToast(errorMessage, {
            title: t("manageTeam.toasts.loadFailed.title"),
            duration: 4000,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching clinic members:", error);
      // Fallback to centralized data only if API call fails
      if (clinicData?.clinic_members) {
        setCurrentVeterinarians(clinicData.clinic_members);
      } else {
        const errorMessage = t("manageTeam.errors.fetchMembers");
        setVetsError(errorMessage);
        showErrorToast(errorMessage, {
          title: t("manageTeam.toasts.loadError.title"),
          duration: 4000,
        });
      }
    } finally {
      setIsLoadingVets(false);
    }
  };

  // Debounced search effect with immediate feedback
  useEffect(() => {
    // If input is empty, clear states and stop
    if (!searchQuery.trim()) {
      setIsSearching(false);
      setSearchResults([]);
      setSearchError("");
      return;
    }

    // Show immediate searching feedback while debouncing the API call
    setIsSearching(true);

    const timeoutId = setTimeout(() => {
      searchVeterinarians(searchQuery);
    }, 500); // 500ms delay

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Clear state when clinicId changes to prevent showing stale data
  useEffect(() => {
    if (clinicId) {
      setCurrentVeterinarians([]);
      setIsLoadingVets(true);
      setVetsError("");
    }
  }, [clinicId]);

  // Load current veterinarians on component mount or when clinicId changes
  useEffect(() => {
    fetchCurrentVeterinarians();
  }, [clinicId]);

  // Function to send invitation
  const sendInvitation = async () => {
    if (!selectedVet) {
      setInvitationError(t("manageTeam.errors.noVetSelected"));
      return;
    }

    setIsSendingInvitation(true);
    setInvitationError("");

    try {
      const token = localStorage.getItem("access_token");

      if (!clinicId) {
        throw new Error(t("manageTeam.errors.missingClinic"));
      }

      const response = await fetch(`${API_BASE_URL}/api/v1/clinic-members`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          clinic_id: clinicId,
          doctor_id: selectedVet.user_id,
          message:
            invitationMessage.trim() ||
            t("manageTeam.invite.defaultMessage"),
        }),
      });

      if (!response.ok) {
        throw new Error(t("manageTeam.errors.inviteFailed"));
      }

      const result = await response.json();
      if (result.status === "success") {
        // Update the search results to show pending status immediately
        setSearchResults((prevResults) =>
          prevResults.map((vet) =>
            vet.user_id === selectedVet.user_id
              ? {
                ...vet,
                doctor_invitation: {
                  id: result.data?.id || Date.now(), // Use API response ID or fallback
                  status: "pending",
                },
              }
              : vet
          )
        );

        // Success - close modal and reset
        setShowInvitationModal(false);
        setSelectedVet(null);
        setInvitationMessage("");

        // Show success toast
        showSuccessToast(
          t("manageTeam.toasts.inviteSuccess.body", {
            name: selectedVet.full_name,
          }),
          {
            title: t("manageTeam.toasts.inviteSuccess.title"),
            duration: 4000,
          }
        );
      } else {
        const errorMessage =
          result.message || t("manageTeam.errors.inviteFailed");
        setInvitationError(errorMessage);
        showErrorToast(errorMessage, {
          title: t("manageTeam.toasts.inviteFailed.title"),
          duration: 5000,
        });
      }
    } catch (error) {
      console.error("Error sending invitation:", error);
      const errorMessage =
        error.message || t("manageTeam.errors.inviteFailed");
      setInvitationError(errorMessage);
      showErrorToast(errorMessage, {
        title: t("manageTeam.toasts.inviteFailed.title"),
        duration: 5000,
      });
    } finally {
      setIsSendingInvitation(false);
    }
  };

  // Function to open invitation modal
  const openInvitationModal = (vet) => {
    setSelectedVet(vet);
    setInvitationMessage(t("manageTeam.invite.defaultMessage"));
    setInvitationError("");
    setShowInvitationModal(true);
  };

  // Function to close invitation modal
  const closeInvitationModal = () => {
    setShowInvitationModal(false);
    setSelectedVet(null);
    setInvitationMessage("");
    setInvitationError("");
  };

  // Function to open cancel confirmation modal
  const openCancelModal = (invitationId) => {
    setInvitationToCancel(invitationId);
    setShowCancelModal(true);
  };

  // Function to close cancel confirmation modal
  const closeCancelModal = () => {
    setShowCancelModal(false);
    setInvitationToCancel(null);
  };

  // Function to open remove confirmation modal
  const openRemoveModal = (memberId) => {
    setMemberToRemove(memberId);
    setShowRemoveModal(true);
  };

  // Function to close remove confirmation modal
  const closeRemoveModal = () => {
    setShowRemoveModal(false);
    setMemberToRemove(null);
  };

  // Function to remove member
  const removeMember = async () => {
    if (!memberToRemove) return;

    setIsRemoving(true);

    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(
        `${API_BASE_URL}/api/v1/clinic-members/${memberToRemove}`,
        {
          method: "DELETE",
          headers: {
            Accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      if (!response.ok) {
        throw new Error(t("manageTeam.errors.removeFailed"));
      }

      const result = await response.json();
      if (result.status === "success") {
        // Update the current veterinarians list by removing the member
        setCurrentVeterinarians((prevMembers) =>
          prevMembers.filter((member) => member.id !== memberToRemove)
        );

        // Show success toast
        showSuccessToast(t("manageTeam.toasts.removeSuccess.body"), {
          title: t("manageTeam.toasts.removeSuccess.title"),
          duration: 3000,
        });

        // Close modal
        closeRemoveModal();
      } else {
        const errorMessage =
          result.message || t("manageTeam.errors.removeFailed");
        showErrorToast(errorMessage, {
          title: t("manageTeam.toasts.removeFailed.title"),
          duration: 5000,
        });
      }
    } catch (error) {
      console.error("Error removing member:", error);
      showErrorToast(t("manageTeam.errors.removeFailed"), {
        title: t("manageTeam.toasts.removeFailed.title"),
        duration: 5000,
      });
    } finally {
      setIsRemoving(false);
    }
  };

  // Function to cancel invitation
  const cancelInvitation = async () => {
    if (!invitationToCancel) return;

    setIsCancelling(true);

    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(
        `${API_BASE_URL}/api/v1/clinic-members/${invitationToCancel}`,
        {
          method: "DELETE",
          headers: {
            Accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      if (!response.ok) {
        throw new Error(t("manageTeam.errors.cancelInvite"));
      }

      const result = await response.json();
      if (result.status === "success") {
        // Update the search results to remove invitation status immediately
        setSearchResults((prevResults) =>
          prevResults.map((vet) =>
            vet.doctor_invitation?.id === invitationToCancel
              ? {
                ...vet,
                doctor_invitation: null,
              }
              : vet
          )
        );

        // Show success toast
        showSuccessToast(t("manageTeam.toasts.cancelSuccess.body"), {
          title: t("manageTeam.toasts.cancelSuccess.title"),
          duration: 3000,
        });

        // Close modal
        closeCancelModal();
      } else {
        const errorMessage =
          result.message || t("manageTeam.errors.cancelInvite");
        showErrorToast(errorMessage, {
          title: t("manageTeam.toasts.cancelFailed.title"),
          duration: 5000,
        });
      }
    } catch (error) {
      console.error("Error cancelling invitation:", error);
      showErrorToast(t("manageTeam.errors.cancelInvite"), {
        title: t("manageTeam.toasts.cancelFailed.title"),
        duration: 5000,
      });
    } finally {
      setIsCancelling(false);
    }
  };

  return (
    <>
      <div
        className="clinicsetup-page-wrapper clinicinfo-member-wrapper"
        dir={i18n.dir()}
        lang={i18n.language}
      >
        <Navbar />
        <div className="reception-backbar" dir={i18n.dir()} lang={i18n.language}>
          <button
            type="button"
            onClick={() => navigate(`/medical/clinic/${clinicId}/setup`)}
            className="reception-backbtn"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '8px' }}>
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            {t("reception.backBar.backToOperations")}
          </button>
        </div>
        <div
          className="clinicsetup-container hide-scrollbar"
          style={{
            background: "#fff",
            borderRadius: "0",
            margin: "0",
            minHeight: "calc(100vh - 100px)",
            boxShadow: "none",
            padding: "0 32px 32px 32px",
            display: "flex",
            flexDirection: "column",
            height: "calc(100vh - 100px)",
            overflow: "auto",
            overscrollBehavior: "contain"
          }}
        >
          <div className="manage-team-header-row">
            <h2 className="clinicsetup-title">{t("manageTeam.title")}</h2>
          </div>
          <p className="manage-team-desc">
            {t("manageTeam.description")}
          </p>

          <div
            className="manage-team-section-row"
            style={{
              marginTop: 8,
              justifyContent: "flex-start",
            }}
          >
            <div
              className="manage-team-section-actions"
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "flex-start",
                gap: "12px",
                width: "100%",
              }}
            >
              <button
                className="manage-team-add-btn"
                onClick={() => {
                  setInviteUserType("VETERINARIAN");
                  setShowEmailInviteModal(true);
                }}
                style={{ background: "#00D4B3", color: "#0b2e2a" }}
              >
                <span className="manage-team-add-btn-plus">+</span>
                {t("manageTeam.buttons.addVeterinarian")}
              </button>
              <button
                className="manage-team-add-btn"
                onClick={() => {
                  setInviteUserType("CLINIC_MODERATOR");
                  setShowEmailInviteModal(true);
                }}
                style={{ background: "#00D4B3", color: "#0b2e2a" }}
              >
                <span className="manage-team-add-btn-plus">+</span>
                {t("manageTeam.buttons.addModerator")}
              </button>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginBottom: "20px",
            }}
          >
            <h3 className="manage-team-section-title">
              {t("manageTeam.current.title")}
            </h3>
            <button
              type="button"
              className="manage-team-viewall"
              onClick={() => setShowViewAllModal(true)}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
              }}
            >
              {t("manageTeam.current.viewAll")}
            </button>
          </div>
          <div className="manage-team-vet-list" style={{ paddingTop: 8 }}>
            {isLoadingVets ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px",
                  color: "#666",
                  fontSize: "16px",
                }}
              >
                Loading current veterinarians...
              </div>
            ) : vetsError ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px",
                  color: "#e53935",
                  fontSize: "16px",
                }}
              >
                {vetsError}
              </div>
            ) : currentVeterinarians.length > 0 ? (
              currentVeterinarians.map((member, idx) => {
                const isOwner =
                  member.is_owner === true ||
                  member.is_owner === "1" ||
                  member.is_owner === 1;

                // Handle both API structure (doctor) and centralized data structure (user)
                const doctor = member.doctor || member.user;
                const doctorName = doctor?.name ||
                  (doctor?.first_name && doctor?.last_name
                    ? `${doctor.first_name} ${doctor.last_name}`.trim()
                    : doctor?.first_name || doctor?.last_name || null);
                const profileImageUrl = doctor?.profile_image_url || member.user?.profile_image_url;

                return (
                  <div key={member.id || idx} className="manage-team-vet-card">
                    <img
                      src={profileImageUrl || "/images/doctor.png"}
                      alt={doctorName || t("manageTeam.current.unknownDoctor")}
                      className="manage-team-vet-img"
                      onError={(e) => (e.target.src = "/images/doctor.png")}
                    />
                    <div className="manage-team-vet-name">
                      {doctorName || t("manageTeam.current.unknownDoctor")}
                    </div>
                    <div className="manage-team-vet-schedule">
                      {isOwner ? (
                        t("manageTeam.current.owner")
                      ) : (
                        <span
                          className={`services-status-${member.status === "active" ? "active" : "inactive"
                            }`}
                        >
                          ●{" "}
                          {t(`manageTeam.status.${member.status}`, {
                            defaultValue:
                              member.status.charAt(0).toUpperCase() +
                              member.status.slice(1),
                          })}
                        </span>
                      )}
                    </div>
                    <div className="manage-team-vet-actions">
                      <a href="#  " className="manage-team-vet-profile">
                        {t("manageTeam.current.viewProfile")}
                      </a>
                      {!isOwner ? (
                        <button
                          className="manage-team-vet-remove"
                          onClick={() => openRemoveModal(member.id)}
                        >
                          {t("manageTeam.current.remove")}
                        </button>
                      ) : (
                        <div className="manage-team-vet-owner">
                          {t("manageTeam.current.owner")}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px",
                  color: "#666",
                  fontSize: "16px",
                }}
              >
                {t("manageTeam.current.empty")}
              </div>
            )}
          </div>

          <h3 className="manage-team-invite-title">
            {t("manageTeam.invite.title")}
          </h3>
          <p className="manage-team-invite-desc">
            {t("manageTeam.invite.description")}
          </p>
          {/* --- Add search, vet cards, and recently invited section below --- */}
          <div className="manage-team-search-row">
            <input
              className="manage-team-search"
              placeholder={t("manageTeam.invite.searchPlaceholder")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div
            className="manage-team-list"
            style={{
              minHeight: "220px",
              paddingTop: 8,
              background: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: "12px",
              padding: "16px",
            }}
          >
            {/* Dynamic search results container always present */}
            {isSearching ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px",
                  color: "#666",
                  fontSize: "16px",
                }}
              >
                {t("manageTeam.invite.searching")}
              </div>
            ) : searchQuery.trim() && searchResults.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px",
                  color: "#666",
                  fontSize: "16px",
                }}
              >
                {searchError || t("manageTeam.invite.noResults")}
              </div>
            ) : searchResults.length > 0 ? (
              searchResults.map((vet) => (
                <div key={vet.user_id} className="manage-team-card">
                  <img
                    src={vet.profile_image_url || "/images/doctor.png"}
                    alt={vet.full_name || t("manageTeam.current.unknownDoctor")}
                    onError={(e) => (e.target.src = "/images/doctor.png")}
                    className="manage-team-avatar"
                  />
                  <div className="manage-team-info">
                    <div className="manage-team-name">{vet.full_name}</div>
                    <div className="manage-team-specialty">
                      {t(`manageTeam.roles.${vet.user_type}`, {
                        defaultValue: vet.user_type,
                      })}
                    </div>
                    <div className="manage-team-location">
                      <span className="manage-team-location-icon">📍</span>
                      {t("manageTeam.invite.userId", { username: vet.username })}
                    </div>
                    {vet.doctor_invitation && (
                      <div
                        style={{
                          marginTop: "8px",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          fontWeight: "500",
                          width: "fit-content",
                          backgroundColor:
                            vet.doctor_invitation.status === "pending"
                              ? "#fff3cd"
                              : vet.doctor_invitation.status === "accepted"
                                ? "#d4edda"
                                : vet.doctor_invitation.status === "rejected"
                                  ? "#f8d7da"
                                  : "#e2e3e5",
                          color:
                            vet.doctor_invitation.status === "pending"
                              ? "#856404"
                              : vet.doctor_invitation.status === "accepted"
                                ? "#155724"
                                : vet.doctor_invitation.status === "rejected"
                                  ? "#721c24"
                                  : "#383d41",
                        }}
                      >
                        {t("manageTeam.invite.invitationStatus", {
                          status: t(
                            `manageTeam.status.${vet.doctor_invitation.status}`,
                            {
                              defaultValue:
                                vet.doctor_invitation.status.charAt(0).toUpperCase() +
                                vet.doctor_invitation.status.slice(1),
                            }
                          ),
                        })}
                      </div>
                    )}
                  </div>
                  {vet.doctor_invitation ? (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                        alignItems: "center",
                      }}
                    >
                      <div
                        style={{
                          padding: "8px 16px",
                          borderRadius: "6px",
                          fontSize: "14px",
                          fontWeight: "500",
                          backgroundColor:
                            vet.doctor_invitation.status === "pending"
                              ? "#fff3cd"
                              : vet.doctor_invitation.status === "accepted"
                                ? "#d4edda"
                                : vet.doctor_invitation.status === "rejected"
                                  ? "#f8d7da"
                                  : "#e2e3e5",
                          color:
                            vet.doctor_invitation.status === "pending"
                              ? "#856404"
                              : vet.doctor_invitation.status === "accepted"
                                ? "#155724"
                                : vet.doctor_invitation.status === "rejected"
                                  ? "#721c24"
                                  : "#383d41",
                          border: "none",
                          cursor: "default",
                        }}
                      >
                        {vet.doctor_invitation.status === "pending" &&
                          t("manageTeam.statusBadges.pending")}
                        {vet.doctor_invitation.status === "accepted" &&
                          t("manageTeam.statusBadges.accepted")}
                        {vet.doctor_invitation.status === "rejected" &&
                          t("manageTeam.statusBadges.rejected")}
                      </div>
                      {vet.doctor_invitation.status === "pending" && (
                        <button
                          className="manage-team-add-btn"
                          style={{
                            background: "#dc3545",
                            fontSize: "12px",
                            padding: "6px 12px",
                          }}
                          onClick={() =>
                            openCancelModal(vet.doctor_invitation.id)
                          }
                        >
                          {t("manageTeam.invite.cancel")}
                        </button>
                      )}
                      {vet.doctor_invitation.status === "rejected" && (
                        <button
                          className="manage-team-add-btn"
                          style={{
                            background: "#00d4b3",
                            fontSize: "12px",
                            padding: "6px 12px",
                          }}
                          onClick={() => openInvitationModal(vet)}
                        >
                          {t("manageTeam.invite.resend")}
                        </button>
                      )}
                    </div>
                  ) : (
                    <button
                      className="manage-team-add-btn"
                      style={{ background: "#00d4b3" }}
                      onClick={() => openInvitationModal(vet)}
                    >
                      <span
                        className="manage-team-add-icon"
                        style={{
                          display: "inline-flex",
                          verticalAlign: "middle",
                          marginRight: "6px",
                        }}
                      >
                        <svg
                          width="28"
                          height="28"
                          viewBox="0 0 28 28"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <circle cx="14" cy="14" r="14" fill="#fff" />
                          <path
                            d="M14 8V20"
                            stroke="#00d4b3"
                            strokeWidth="2.2"
                            strokeLinecap="round"
                          />
                          <path
                            d="M8 14H20"
                            stroke="#00d4b3"
                            strokeWidth="2.2"
                            strokeLinecap="round"
                          />
                        </svg>
                      </span>{" "}
                      {t("manageTeam.invite.addToClinic")}
                    </button>
                  )}
                </div>
              ))
            ) : (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px",
                  color: "#666",
                  fontSize: "16px",
                }}
              >
                {t("manageTeam.invite.noQuery")}
              </div>
            )}
          </div>
          {/* Recently invited section removed */}
          {/* 
          <div className="manage-team-recently-invited-label">
            {t("manageTeam.invite.recentlyInvited")}
          </div>
          <div className="manage-team-invited-list">
            Recently invited veterinarians will be rendered here from API data
          </div>
          */}
        </div>
      </div>

      {showViewAllModal && (
        <ViewAllVetsModal
          open={showViewAllModal}
          onClose={() => setShowViewAllModal(false)}
          veterinarians={currentVeterinarians.map((member) => {
            // Handle both API structure (doctor) and centralized data structure (user)
            const doctor = member.doctor || member.user;
            const doctorName = doctor?.name ||
              (doctor?.first_name && doctor?.last_name
                ? `${doctor.first_name} ${doctor.last_name}`.trim()
                : doctor?.first_name || doctor?.last_name || null);
            const profileImageUrl = doctor?.profile_image_url || member.user?.profile_image_url;

            return {
              name: doctorName || t("manageTeam.current.unknownDoctor"),
              specialty: member.role || "VETERINARIAN",
              schedule: t("manageTeam.current.statusLabel", {
                status: t(`manageTeam.status.${member.status}`, {
                  defaultValue:
                    member.status.charAt(0).toUpperCase() +
                    member.status.slice(1),
                }),
              }),
              img: profileImageUrl || "/images/doctor.png",
            };
          })}
          clinicId={clinicId}
        />
      )}

      {/* Email Invitation Modal */}
      {showEmailInviteModal && (
        <InviteVetByEmailModal
          open={showEmailInviteModal}
          onClose={() => setShowEmailInviteModal(false)}
          clinicId={clinicId}
          userType={inviteUserType}
        />
      )}

      {/* Invitation Modal */}
      {showInvitationModal && (
        <div
          className="modal-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            backdropFilter: "blur(2px)",
            margin: 0,
            padding: 0,
            overflow: "hidden",
          }}
          dir={i18n.dir()}
          lang={i18n.language}
        >
          <div
            className="modal-content manage-team-modal"
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              padding: "24px",
              maxWidth: "500px",
              width: "90%",
              maxHeight: "80vh",
              overflow: "auto",
            }}
            dir={i18n.dir()}
            lang={i18n.language}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontSize: "20px",
                  fontWeight: "600",
                  color: "#333",
                }}
              >
                {t("manageTeam.invite.modal.title")}
              </h3>
              <button
                onClick={closeInvitationModal}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  color: "#666",
                }}
              >
                ×
              </button>
            </div>

            {selectedVet && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "20px",
                  padding: "12px",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "8px",
                }}
              >
                <img
                  src={selectedVet.profile_image_url || "/images/doctor.png"}
                  alt={selectedVet.full_name}
                  onError={(e) => (e.target.src = "/images/doctor.png")}
                  style={{
                    width: "50px",
                    height: "50px",
                    borderRadius: "50%",
                    marginRight: "12px",
                  }}
                />
                <div>
                  <div style={{ fontWeight: "600", fontSize: "16px" }}>
                    {selectedVet.full_name}
                  </div>
                  <div style={{ color: "#666", fontSize: "14px" }}>
                    {t(`manageTeam.roles.${selectedVet.user_type}`, {
                      defaultValue: selectedVet.user_type,
                    })}
                  </div>
                </div>
              </div>
            )}

            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "500",
                  color: "#333",
                }}
              >
                {t("manageTeam.invite.modal.messageLabel")}
              </label>
              <textarea
                value={invitationMessage}
                onChange={(e) => setInvitationMessage(e.target.value)}
                style={{
                  width: "100%",
                  minHeight: "120px",
                  padding: "12px",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  fontSize: "14px",
                  resize: "vertical",
                  fontFamily: "inherit",
                }}
              />
            </div>

            {invitationError && (
              <div
                style={{
                  color: "#e53935",
                  fontSize: "14px",
                  marginBottom: "16px",
                  padding: "8px",
                  backgroundColor: "#ffebee",
                  borderRadius: "4px",
                }}
              >
                {invitationError}
              </div>
            )}

            <div
              className="manage-team-modal-actions"
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={closeInvitationModal}
                className="manage-team-modal-btn manage-team-modal-btn-cancel"
                style={{
                  padding: "10px 20px",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  background: "white",
                  color: "#666",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
                disabled={isSendingInvitation}
              >
                {t("manageTeam.buttons.cancel")}
              </button>
              <button
                onClick={sendInvitation}
                disabled={isSendingInvitation}
                className="manage-team-modal-btn manage-team-modal-btn-primary"
                style={{
                  padding: "10px 20px",
                  border: "none",
                  borderRadius: "6px",
                  background: isSendingInvitation ? "#ccc" : "#00d4b3",
                  color: "white",
                  cursor: isSendingInvitation ? "not-allowed" : "pointer",
                  fontSize: "14px",
                }}
              >
                {isSendingInvitation
                  ? t("manageTeam.invite.modal.sending")
                  : t("manageTeam.invite.modal.send")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      {showCancelModal && (
        <div
          className="modal-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            backdropFilter: "blur(2px)",
            margin: 0,
            padding: 0,
            overflow: "hidden",
          }}
          dir={i18n.dir()}
          lang={i18n.language}
        >
          <div
            className="modal-content"
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              padding: "24px",
              maxWidth: "400px",
              width: "90%",
              textAlign: "center",
            }}
            dir={i18n.dir()}
            lang={i18n.language}
          >
            <div
              style={{
                marginBottom: "20px",
              }}
            >
              <div
                style={{
                  width: "60px",
                  height: "60px",
                  borderRadius: "50%",
                  backgroundColor: "#ffebee",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 16px",
                }}
              >
                <svg
                  width="30"
                  height="30"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                    stroke="#dc3545"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3
                style={{
                  margin: 0,
                  fontSize: "20px",
                  fontWeight: "600",
                  color: "#333",
                  marginBottom: "8px",
                }}
              >
                {t("manageTeam.cancelModal.title")}
              </h3>
              <p
                style={{
                  margin: 0,
                  fontSize: "16px",
                  color: "#666",
                  lineHeight: "1.5",
                }}
              >
                {t("manageTeam.cancelModal.body")}
              </p>
            </div>

            <div
              className="manage-team-modal-actions"
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "center",
              }}
            >
              <button
                onClick={closeCancelModal}
                className="manage-team-modal-btn manage-team-modal-btn-cancel"
                style={{
                  padding: "10px 20px",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  background: "white",
                  color: "#666",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
                disabled={isCancelling}
              >
                {t("manageTeam.cancelModal.keep")}
              </button>
              <button
                onClick={cancelInvitation}
                disabled={isCancelling}
                className="manage-team-modal-btn manage-team-modal-btn-danger"
                style={{
                  padding: "10px 20px",
                  border: "none",
                  borderRadius: "6px",
                  background: isCancelling ? "#ccc" : "#dc3545",
                  color: "white",
                  cursor: isCancelling ? "not-allowed" : "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                {isCancelling
                  ? t("manageTeam.cancelModal.cancelling")
                  : t("manageTeam.cancelModal.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove Confirmation Modal */}
      {showRemoveModal && (
        <div
          className="modal-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            backdropFilter: "blur(2px)",
            margin: 0,
            padding: 0,
            overflow: "hidden",
          }}
          dir={i18n.dir()}
          lang={i18n.language}
        >
          <div
            className="modal-content"
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              padding: "24px",
              maxWidth: "400px",
              width: "90%",
              textAlign: "center",
            }}
            dir={i18n.dir()}
            lang={i18n.language}
          >
            <div
              style={{
                marginBottom: "20px",
              }}
            >
              <div
                style={{
                  width: "60px",
                  height: "60px",
                  borderRadius: "50%",
                  backgroundColor: "#ffebee",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 16px",
                }}
              >
                <svg
                  width="30"
                  height="30"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                    stroke="#dc3545"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3
                style={{
                  margin: 0,
                  fontSize: "20px",
                  fontWeight: "600",
                  color: "#333",
                  marginBottom: "8px",
                }}
              >
                {t("manageTeam.removeModal.title")}
              </h3>
              <p
                style={{
                  margin: 0,
                  fontSize: "16px",
                  color: "#666",
                  lineHeight: "1.5",
                }}
              >
                {t("manageTeam.removeModal.body")}
              </p>
            </div>

            <div
              className="manage-team-modal-actions"
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "center",
              }}
            >
              <button
                onClick={closeRemoveModal}
                className="manage-team-modal-btn manage-team-modal-btn-cancel"
                style={{
                  padding: "10px 20px",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  background: "white",
                  color: "#666",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
                disabled={isRemoving}
              >
                {t("manageTeam.removeModal.keep")}
              </button>
              <button
                onClick={removeMember}
                disabled={isRemoving}
                className="manage-team-modal-btn manage-team-modal-btn-danger"
                style={{
                  padding: "10px 20px",
                  border: "none",
                  borderRadius: "6px",
                  background: isRemoving ? "#ccc" : "#dc3545",
                  color: "white",
                  cursor: isRemoving ? "not-allowed" : "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                {isRemoving
                  ? t("manageTeam.removeModal.removing")
                  : t("manageTeam.removeModal.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ManageTeam;
